import React from 'react';
import { Search, ChevronLeft, ChevronRight, ChevronUp, ChevronDown } from 'lucide-react';

interface Column {
    key: string;
    label: string;
    render?: (value: any, item: any) => React.ReactNode;
    sortable?: boolean;
}

type SortDirection = 'asc' | 'desc' | null;

interface DataTableProps {
    data: any[];
    columns: Column[];
    searchPlaceholder?: string;
    searchValue: string;
    onSearchChange: (value: string) => void;
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onEdit?: (item: any) => void;
    editButtonText?: string;
    // selection
    selectable?: boolean;
    selectedIds?: string[];
    onToggleSelect?: (id: string, checked: boolean) => void;
    onToggleSelectAll?: (checked: boolean) => void;
    // delete
    onDelete?: (item: any) => void;
    deleteButtonText?: string;
    // custom actions
    actionsRenderer?: (item: any) => React.ReactNode;
    // sorting
    sortColumn?: string;
    sortDirection?: SortDirection;
    onSort?: (column: string, direction: SortDirection) => void;
    // compact / dense layout
    dense?: boolean;
    /** When set, renders card layout on small screens instead of the table. */
    mobileCardRenderer?: (item: any) => React.ReactNode;
}

const DataTable: React.FC<DataTableProps> = ({
    data,
    columns,
    searchPlaceholder = 'Buscar...',
    searchValue,
    onSearchChange,
    currentPage,
    totalPages,
    onPageChange,
    onEdit,
    editButtonText = 'Editar',
    selectable = false,
    selectedIds = [],
    onToggleSelect,
    onToggleSelectAll,
    onDelete,
    deleteButtonText = 'Excluir',
    actionsRenderer,
    sortColumn,
    sortDirection,
    onSort,
    dense = false,
    mobileCardRenderer,
}) => {
    const allSelected = selectable && data.length > 0 && selectedIds.length === data.length;
    const someSelected = selectable && selectedIds.length > 0 && selectedIds.length < data.length;

    const handleSort = (columnKey: string) => {
        if (!onSort) return;

        const column = columns.find(col => col.key === columnKey);
        if (!column?.sortable) return;

        let newDirection: SortDirection = 'asc';
        if (sortColumn === columnKey) {
            if (sortDirection === 'asc') {
                newDirection = 'desc';
            } else if (sortDirection === 'desc') {
                newDirection = null;
            }
        }

        onSort(columnKey, newDirection);
    };

    const getSortIcon = (columnKey: string) => {
        if (sortColumn !== columnKey) {
            return <ChevronUp className="h-4 w-4 text-gray-300" />;
        }

        if (sortDirection === 'asc') {
            return <ChevronUp className="h-4 w-4 text-gray-600" />;
        } else if (sortDirection === 'desc') {
            return <ChevronDown className="h-4 w-4 text-gray-600" />;
        }

        return <ChevronUp className="h-4 w-4 text-gray-300" />;
    };

    return (
        <div className="bg-white shadow-sm rounded-lg">
            {/* Search */}
            <div className={dense ? 'p-3 sm:p-2 border-b border-secondary-200' : 'p-4 sm:p-6 border-b border-secondary-200'}>
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
                    <input
                        type="text"
                        placeholder={searchPlaceholder}
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        className={dense ? 'w-full pl-8 pr-3 py-1.5 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm' : 'w-full pl-10 pr-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'}
                    />
                </div>
            </div>

            {/* Mobile cards */}
            {mobileCardRenderer && (
                <div className="md:hidden divide-y divide-secondary-200">
                    {data.length === 0 ? (
                        <div className="px-4 py-10 text-center text-secondary-500 text-sm">
                            Nenhum item encontrado
                        </div>
                    ) : (
                        data.map((item, index) => (
                            <div key={item.id || index}>{mobileCardRenderer(item)}</div>
                        ))
                    )}
                </div>
            )}

            {/* Table */}
            <div className={`overflow-x-auto ${mobileCardRenderer ? 'hidden md:block' : ''}`}>
                <table className="min-w-full divide-y divide-secondary-200">
                    <thead className="bg-secondary-50">
                        <tr>
                            {selectable && (
                                <th className={dense ? 'table-header w-10 py-1 px-2 text-xs' : 'table-header w-12'}>
                                    <input
                                        type="checkbox"
                                        checked={allSelected}
                                        ref={(el) => {
                                            if (el) el.indeterminate = Boolean(someSelected);
                                        }}
                                        onChange={(e) => onToggleSelectAll && onToggleSelectAll(e.target.checked)}
                                    />
                                </th>
                            )}
                            {columns.map((column) => (
                                <th key={column.key} className={dense ? 'table-header py-1 px-2 text-xs' : 'table-header'}>
                                    <div className={dense ? 'flex items-center space-x-1 text-xs' : 'flex items-center space-x-1'}>
                                        <span>{column.label}</span>
                                        {column.sortable && (
                                            <button
                                                onClick={() => handleSort(column.key)}
                                                className="flex items-center hover:text-gray-600 transition-colors"
                                            >
                                                {getSortIcon(column.key)}
                                            </button>
                                        )}
                                    </div>
                                </th>
                            ))}
                            {(actionsRenderer || onEdit || onDelete) && (
                                <th className={dense ? 'table-header py-1 px-2 text-xs' : 'table-header'}>
                                    Ações
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-secondary-200">
                        {data.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={columns.length + (onEdit || onDelete ? 1 : 0) + (selectable ? 1 : 0)}
                                    className={dense ? 'px-2 py-6 text-center text-secondary-500 text-sm' : 'px-6 py-12 text-center text-secondary-500'}
                                >
                                    Nenhum item encontrado
                                </td>
                            </tr>
                        ) : (
                            data.map((item, index) => (
                                <tr key={item.id || index} className={dense ? 'hover:bg-secondary-50 text-sm' : 'hover:bg-secondary-50'}>
                                    {selectable && (
                                        <td className={dense ? 'table-cell w-10 py-1 px-2' : 'table-cell w-12'}>
                                            <input
                                                type="checkbox"
                                                checked={selectedIds.includes(item.id)}
                                                onChange={(e) => onToggleSelect && onToggleSelect(item.id, e.target.checked)}
                                            />
                                        </td>
                                    )}
                                    {columns.map((column) => (
                                        <td key={column.key} className={dense ? 'table-cell py-1 px-2' : 'table-cell'}>
                                            {column.render
                                                ? column.render(item[column.key], item)
                                                : item[column.key]
                                            }
                                        </td>
                                    ))}
                                    {(actionsRenderer || onEdit || onDelete) && (
                                        <td className={dense ? 'table-cell space-x-2 py-1 px-2' : 'table-cell space-x-2'}>
                                            {actionsRenderer
                                                ? actionsRenderer(item)
                                                : (
                                                    <>
                                                        {onEdit && (
                                                            <button
                                                                onClick={() => onEdit(item)}
                                                                className={dense ? 'btn-primary text-xs py-1 px-2' : 'btn-primary text-sm'}
                                                            >
                                                                {editButtonText}
                                                            </button>
                                                        )}
                                                        {onDelete && (
                                                            <button
                                                                onClick={() => onDelete(item)}
                                                                className={dense ? 'btn-secondary text-xs py-1 px-2' : 'btn-secondary text-sm'}
                                                            >
                                                                {deleteButtonText}
                                                            </button>
                                                        )}
                                                    </>
                                                )}
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {data.length > 0 && (
                <div className={dense ? 'px-3 sm:px-2 py-3 sm:py-2 border-t border-secondary-200 flex items-center justify-between gap-3' : 'px-4 sm:px-6 py-3 sm:py-4 border-t border-secondary-200 flex items-center justify-between gap-3'}>
                    <div className={dense ? 'text-xs text-secondary-700' : 'text-sm text-secondary-700'}>
                        Página {currentPage} de {totalPages}
                    </div>
                    <div className="flex space-x-2">
                        <button
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            className={dense ? 'p-1.5 rounded-lg border border-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50' : 'p-2 rounded-lg border border-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50'}
                        >
                            <ChevronLeft className={dense ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                        </button>
                        <button
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            className={dense ? 'p-1.5 rounded-lg border border-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50' : 'p-2 rounded-lg border border-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-secondary-50'}
                        >
                            <ChevronRight className={dense ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataTable;

